import React from 'react';
import { FleetSummary } from '../../types/vehicle';

interface FleetSummaryCardsProps {
  summary: FleetSummary | null;
  loading?: boolean;
}

const FleetSummaryCards: React.FC<FleetSummaryCardsProps> = ({ summary, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i}>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-8 bg-gray-200 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!summary) return null;

  const totalVehicles = summary.totalVehicles || 0;
  const available = summary.availableVehicles || 0;
  const rented = summary.rentedVehicles || 0;
  const maintenance = summary.maintenanceVehicles || 0;
  const outOfService = summary.outOfServiceVehicles || 0;
  const reserved = Math.max(0, totalVehicles - available - rented - maintenance - outOfService);
  const utilizationRate = summary.utilizationRate || 0;
  const maintenanceDue = summary.maintenanceDueCount || 0;

  // Calculate percentages for the visual bar
  const availablePercent = totalVehicles > 0 ? (available / totalVehicles) * 100 : 0;
  const rentedPercent = totalVehicles > 0 ? (rented / totalVehicles) * 100 : 0;
  const maintenancePercent = totalVehicles > 0 ? (maintenance / totalVehicles) * 100 : 0;
  const outOfServicePercent = totalVehicles > 0 ? (outOfService / totalVehicles) * 100 : 0;
  const reservedPercent = totalVehicles > 0 ? (reserved / totalVehicles) * 100 : 0;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
      {/* Header Section */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Fleet Overview</h2>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
              <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
            </svg>
            <span className="font-medium">{totalVehicles}</span>
            <span>Total Vehicles</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 divide-x divide-gray-200">
        {/* Available */}
        <div className="px-6 py-5 hover:bg-green-50/50 transition-colors cursor-pointer group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Available</p>
          </div>
          <p className="text-2xl font-bold text-green-600">{available}</p>
          <p className="text-xs text-gray-500 mt-1">{availablePercent.toFixed(0)}% of fleet</p>
        </div>

        {/* Rented */}
        <div className="px-6 py-5 hover:bg-purple-50/50 transition-colors cursor-pointer group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-purple-500"></div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Rented</p>
          </div>
          <p className="text-2xl font-bold text-purple-600">{rented}</p>
          <p className="text-xs text-gray-500 mt-1">{rentedPercent.toFixed(0)}% of fleet</p>
        </div>

        {/* Reserved */}
        <div className="px-6 py-5 hover:bg-blue-50/50 transition-colors cursor-pointer group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Reserved</p>
          </div>
          <p className="text-2xl font-bold text-blue-600">{reserved}</p>
          <p className="text-xs text-gray-500 mt-1">{reservedPercent.toFixed(0)}% of fleet</p>
        </div>

        {/* Maintenance */}
        <div className="px-6 py-5 hover:bg-orange-50/50 transition-colors cursor-pointer group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-orange-500"></div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Maintenance</p>
          </div>
          <p className="text-2xl font-bold text-orange-600">{maintenance}</p>
          <p className="text-xs text-gray-500 mt-1">{maintenancePercent.toFixed(0)}% of fleet</p>
        </div>

        {/* Out of Service */}
        <div className="px-6 py-5 hover:bg-red-50/50 transition-colors cursor-pointer group">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Out of Service</p>
          </div>
          <p className="text-2xl font-bold text-red-600">{outOfService}</p>
          <p className="text-xs text-gray-500 mt-1">{outOfServicePercent.toFixed(0)}% of fleet</p>
        </div>

        {/* Utilization Rate */}
        <div className="px-6 py-5 hover:bg-indigo-50/50 transition-colors cursor-pointer group">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-3 h-3 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
            </svg>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Utilization</p>
          </div>
          <p className="text-2xl font-bold text-indigo-600">{utilizationRate.toFixed(1)}%</p>
          <p className="text-xs text-gray-500 mt-1">Fleet efficiency</p>
        </div>

        {/* Maintenance Due */}
        <div className="px-6 py-5 hover:bg-yellow-50/50 transition-colors cursor-pointer group">
          <div className="flex items-center gap-2 mb-1">
            <svg className="w-3 h-3 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Due Soon</p>
          </div>
          <p className="text-2xl font-bold text-yellow-600">{maintenanceDue}</p>
          <p className="text-xs text-gray-500 mt-1">Within 7 days</p>
        </div>
      </div>

      {/* Visual Status Bar */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center gap-3 mb-2">
          <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Fleet Distribution</p>
        </div>
        <div className="flex h-3 rounded-full overflow-hidden bg-gray-200">
          {available > 0 && (
            <div 
              className="bg-green-500 hover:bg-green-600 transition-colors" 
              style={{ width: `${availablePercent}%` }}
              title={`Available: ${available} (${availablePercent.toFixed(1)}%)`}
            ></div>
          )}
          {rented > 0 && (
            <div 
              className="bg-purple-500 hover:bg-purple-600 transition-colors" 
              style={{ width: `${rentedPercent}%` }}
              title={`Rented: ${rented} (${rentedPercent.toFixed(1)}%)`}
            ></div>
          )}
          {reserved > 0 && (
            <div 
              className="bg-blue-500 hover:bg-blue-600 transition-colors" 
              style={{ width: `${reservedPercent}%` }}
              title={`Reserved: ${reserved} (${reservedPercent.toFixed(1)}%)`}
            ></div>
          )}
          {maintenance > 0 && (
            <div 
              className="bg-orange-500 hover:bg-orange-600 transition-colors" 
              style={{ width: `${maintenancePercent}%` }}
              title={`Maintenance: ${maintenance} (${maintenancePercent.toFixed(1)}%)`}
            ></div>
          )}
          {outOfService > 0 && (
            <div 
              className="bg-red-500 hover:bg-red-600 transition-colors" 
              style={{ width: `${outOfServicePercent}%` }}
              title={`Out of Service: ${outOfService} (${outOfServicePercent.toFixed(1)}%)`}
            ></div>
          )}
        </div>
        <div className="flex flex-wrap gap-4 mt-3">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-green-500"></div>
            <span className="text-xs text-gray-600">Available</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-purple-500"></div>
            <span className="text-xs text-gray-600">Rented</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-blue-500"></div>
            <span className="text-xs text-gray-600">Reserved</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-orange-500"></div>
            <span className="text-xs text-gray-600">Maintenance</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-red-500"></div>
            <span className="text-xs text-gray-600">Out of Service</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetSummaryCards;
